import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listUserCampaignsForDebug } from "@/lib/campaign-pixel-hits.functions";
import { getOrCreateCampaignAlias } from "@/lib/campaign-alias.functions";
import { getPerfectSnippetStats } from "@/lib/perfect-snippet-stats.functions";
import { validateTrackingSnippet } from "@/lib/snippet-validation";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { CheckCircle2, Copy, AlertTriangle, Info, Activity, Skull } from "lucide-react";

export const Route = createFileRoute("/_authenticated/perfect-snippet")({
  component: PerfectSnippetPage,
});

const ORIGIN = "https://meineasykredit.click";
const DEFAULT_ASSET_HOST_SUGGESTIONS = [
  "meineasykredit.click",
  "img.meineasykredit.click",
  "assets.meineasykredit.click",
  "media.meineasykredit.click",
];

type SnippetStyle =
  | "neutral"
  | "footer-brand"
  | "footer-divider"
  | "signature-avatar"
  | "stealth-bg"
  | "stealth-nested"
  | "stealth-mso"
  | "stealth-rotate"
  | "logo";

const STYLE_LABELS: Record<SnippetStyle, string> = {
  logo: "Logo-Footer (Produktion — sichtbares 20×20 Icon + Markentext, wirkt wie normaler Footer)",
  neutral: "Neutral (CSS-only, wirkt wie unsichtbares Content-Asset)",
  "footer-brand": "Footer-Brand-Tagline (wirkt wie Mailtool-Footer)",
  "footer-divider": "Footer-Divider (Hairline + Mini-Bullet)",
  "signature-avatar": "Signatur-Avatar (24×24 Logo)",
  "stealth-bg": "Stealth A (Legacy) — CSS-Background im <span>",
  "stealth-nested": "Stealth B (Legacy) — verschachtelte Tables",
  "stealth-mso": "Stealth C (Legacy) — MSO-Conditional",
  "stealth-rotate": "Stealth Rotation (Legacy) — A/B/C zufaellig",
};

function normalizeAssetHost(input: string): string {
  const v = (input || "").trim().toLowerCase();
  if (!v) return ORIGIN;
  if (/^https?:\/\//.test(v)) return v.replace(/\/+$/, "");
  return `https://${v.replace(/\/+$/, "")}`;
}

function buildPixelUrl(token: string, assetOrigin: string, shortPath: boolean): string {
  if (shortPath) {
    // Stealth-Pfad: keine Tracking-Keywords, keine Empfaenger-Mail in der
    // URL. Wirkt wie ein gewoehnliches Asset. Pro-Empfaenger-Aufloesung
    // funktioniert dann nur ueber den opaken Token (1 Token pro Kampagne).
    return `${assetOrigin}/a/${token}.png`;
  }
  // Plusvibe-Merge-Tags: {{sender_email}}, {{email}} (Empfaenger), {{lead_id}} (Cache-Buster).
  return `${assetOrigin}/api/public/i/${token}.png?s={{sender_email}}&r={{email}}&m={{lead_id}}`;
}

function buildClickUrl(token: string, targetUrl: string): string {
  const enc = btoa(targetUrl).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `${ORIGIN}/api/public/r/${token}?u=${enc}`;
}

function stealthBg(url: string, brand: string): string {
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
    `style="margin-top:20px;border-collapse:collapse">` +
    `<tr><td align="center" style="border-top:1px solid #e5e7eb;padding:12px 0 0;` +
    `font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
    `<a href="https://meineasykredit.de/" target="_blank" rel="noopener" ` +
    `style="color:#9ca3af;text-decoration:none;display:inline-block">` +
    `<span style="display:inline-block;width:20px;height:20px;vertical-align:middle;` +
    `margin-right:6px;background:url('${url}') center/contain no-repeat" aria-hidden="true"></span>` +
    `<span style="vertical-align:middle">Gesendet mit ${brand}</span>` +
    `</a></td></tr></table>`
  );
}

function stealthNested(url: string, brand: string): string {
  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="margin-top:20px"><tr><td align="center" style="padding:0">` +
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-top:1px solid #e5e7eb;max-width:320px;width:100%"><tr>` +
    `<td align="center" style="padding:12px 8px 0;font-family:Arial,Helvetica,sans-serif;` +
    `font-size:11px;line-height:14px;color:#9ca3af">` +
    `<a href="https://meineasykredit.de/" style="text-decoration:none;color:#9ca3af" ` +
    `target="_blank" rel="noopener">` +
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr>` +
    `<td width="20" height="20" style="line-height:0;font-size:0">` +
    `<img src="${url}" alt="${brand}" width="20" height="20" border="0" ` +
    `style="display:block;border:0;outline:none;text-decoration:none" />` +
    `</td><td style="padding-left:6px;font-size:11px;line-height:14px;vertical-align:middle">` +
    `Gesendet mit ${brand}</td></tr></table></a></td></tr></table></td></tr></table>`
  );
}

function stealthMso(url: string, brand: string): string {
  return (
    `<!--[if mso]>` +
    `<table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0">` +
    `<tr><td style="border-top:1px solid #e5e7eb;padding-top:12px">` +
    `<![endif]-->` +
    `<div style="margin-top:20px;border-top:1px solid #e5e7eb;padding:12px 0 0;` +
    `text-align:center;font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
    `<a href="https://meineasykredit.de/" target="_blank" rel="noopener" ` +
    `style="color:#9ca3af;text-decoration:none">` +
    `<img src="${url}" width="20" height="20" alt="${brand}" ` +
    `style="border:0;vertical-align:middle;margin-right:6px" />` +
    `Gesendet mit ${brand}</a></div>` +
    `<!--[if mso]></td></tr></table><![endif]-->`
  );
}

function buildSnippet(style: SnippetStyle, url: string, brand: string): string {
  const safeBrand = brand.replace(/</g, "&lt;").replace(/>/g, "&gt;").slice(0, 60) || "easymail";
  switch (style) {
    case "logo":
      // Empfohlene Produktions-Variante: sichtbares 20x20 Footer-Icon mit
      // echtem alt-Text + Footer-Link. Wirkt fuer Spamfilter wie ein
      // normales Footer-Asset, nicht wie ein Tracking-Pixel.
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="margin-top:20px;border-collapse:collapse">` +
        `<tr><td align="center" style="border-top:1px solid #e5e7eb;padding:12px 0 0;` +
        `font:12px/16px Arial,Helvetica,sans-serif;color:#6b7280">` +
        `<img src="${url}" alt="${safeBrand}" width="20" height="20" border="0" ` +
        `style="display:inline-block;vertical-align:middle;border:0;margin-right:8px" />` +
        `<span style="vertical-align:middle">${safeBrand}</span>` +
        `</td></tr></table>`
      );
    case "neutral":
      // Produktions-Snippet: ein einzelnes <img>, keinerlei Branding,
      // KEINE width/height-HTML-Attribute (nur CSS), kein Wrapper, keine
      // Tracking-typischen Keywords im Markup. Wirkt wie ein normales
      // eingebettetes Content-Asset.
      return (
        `<img src="${url}" alt="" role="presentation" ` +
        `style="border:0;outline:0;width:1px;height:1px" />`
      );
    case "footer-divider":
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="margin-top:16px;border-collapse:collapse">` +
        `<tr><td style="border-top:1px solid #e5e7eb;padding-top:8px;text-align:center;` +
        `font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
        `<img src="${url}" alt="" width="6" height="6" ` +
        `style="vertical-align:middle;border:0;border-radius:50%;opacity:.6" />` +
        `</td></tr></table>`
      );
    case "signature-avatar":
      return (
        `<img src="${url}" alt="Logo" width="24" height="24" ` +
        `style="display:inline-block;border:0;border-radius:50%;vertical-align:middle" />`
      );
    case "footer-brand":
    default:
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="margin-top:20px;border-collapse:collapse">` +
        `<tr><td style="border-top:1px solid #e5e7eb;padding:10px 0 0;text-align:center;` +
        `font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
        `<img src="${url}" alt="" width="14" height="14" ` +
        `style="vertical-align:middle;border:0;border-radius:2px;opacity:.7" />` +
        `<span style="vertical-align:middle;margin-left:6px">Gesendet mit ${safeBrand}</span>` +
        `</td></tr></table>`
      );
    case "stealth-bg":
      return stealthBg(url, safeBrand);
    case "stealth-nested":
      return stealthNested(url, safeBrand);
    case "stealth-mso":
      return stealthMso(url, safeBrand);
    case "stealth-rotate": {
      const variants = [stealthBg, stealthNested, stealthMso];
      const pick = variants[Math.floor(Math.random() * variants.length)];
      return pick(url, safeBrand);
    }
  }
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert`);
  } catch {
    toast.error("Kopieren fehlgeschlagen");
  }
}

function PerfectSnippetPage() {
  const listFn = useServerFn(listUserCampaignsForDebug);
  const aliasFn = useServerFn(getOrCreateCampaignAlias);
  const statsFn = useServerFn(getPerfectSnippetStats);

  const campsQ = useQuery({
    queryKey: ["perfect-snippet-campaigns"],
    queryFn: () => listFn(),
  });

  const [campaignId, setCampaignId] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [style, setStyle] = useState<SnippetStyle>("logo");
  const [brand, setBrand] = useState<string>("easymail");
  const [assetHost, setAssetHost] = useState<string>("meineasykredit.click");
  const [shortPath, setShortPath] = useState<boolean>(true);
  const [clickTarget, setClickTarget] = useState<string>("https://example.com/landing");
  const [loadingAlias, setLoadingAlias] = useState(false);

  async function loadAlias(id: string) {
    setCampaignId(id);
    setToken("");
    if (!id) return;
    setLoadingAlias(true);
    try {
      const r = await aliasFn({ data: { campaignId: id } });
      setToken(r.shortToken ?? "");
    } catch (e) {
      toast.error(
        `Alias konnte nicht geladen werden: ${e instanceof Error ? e.message : "Fehler"}`,
      );
    } finally {
      setLoadingAlias(false);
    }
  }

  const assetOrigin = useMemo(() => normalizeAssetHost(assetHost), [assetHost]);
  const pixelUrl = token ? buildPixelUrl(token, assetOrigin, shortPath) : "";
  const snippet = token ? buildSnippet(style, pixelUrl, brand) : "";
  const clickUrl = token && clickTarget ? buildClickUrl(token, clickTarget) : "";
  const unsubscribeUrl = token ? `${ORIGIN}/api/public/u/{{lead_id}}` : "";

  const validation = useMemo(() => (snippet ? validateTrackingSnippet(snippet) : null), [snippet]);

  const statsQ = useQuery({
    queryKey: ["perfect-snippet-stats", campaignId],
    queryFn: () => statsFn({ data: { campaignId } }),
    enabled: Boolean(campaignId),
    refetchInterval: 30_000,
  });

  const bounceWebhookUrl = `${ORIGIN}/api/public/bounce-webhook`;
  const samplePayload = token
    ? JSON.stringify(
        {
          tag: token,
          recipient_email: "empfaenger@example.com",
          sender_email: "absender@deinedomain.de",
          bounce_type: "hard",
          bounce_reason: "550 5.1.1 User unknown",
          provider: "plusvibe",
          message_id: "<abc@mx.example>",
          diagnostic_code: "smtp; 550 5.1.1 The email account does not exist",
          remote_mta: "mx.empfaenger.de",
          status_code: "5.1.1",
          action: "failed",
          subject: "Re: dein Angebot",
        },
        null,
        2,
      )
    : "";

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Perfect Snippet"
        description="All-in-one HTML-Snippet + Bounce-Webhook für externe Versand-Tools (Plusvibe & Co.): getarntes Open-Tracking, Bounce-Erkennung mit Telegram-Alarm, Click-Wrapper, List-Unsubscribe — alles live im Admin-Panel."
      />

      <HelpPanel
        title="Hilfe: Was ist das hier und wie nutze ich es?"
        what={
          <>
            Diese Seite gibt dir ein <b>fertiges Stück HTML-Code</b>, das du in dein Versand-Tool
            (Plusvibe, Mailchimp, Lemlist …) einfügen kannst. Der Code misst Öffnungen, erkennt
            Bounces und baut einen sauberen Abmelde-Link ein — alles auf einmal.
          </>
        }
        why={
          <>
            Externe Tools tracken oft mit hässlichen URLs, die Spam-Filter sofort erkennen. Unser
            Snippet sieht aus wie ein normales Logo-Bild und wird daher viel seltener blockiert —
            deine Öffnungs-Statistik wird ehrlicher und mehr Mails landen im Posteingang.
          </>
        }
        how={[
          <>
            <b>Kampagne wählen</b> aus der Liste rechts (oder neu anlegen unter "Kampagnen").
          </>,
          <>
            <b>Tracking-Modus entscheiden:</b>
            <br />• <b>Pro Kampagne</b> — eine URL für alle, beste Zustellbarkeit, nur Gesamt-Zahl.
            <br />• <b>Pro Mail</b> — eigene URL je Empfänger, du siehst wer wann geöffnet hat (für
            A/B).
            <br />
            Wenn unsicher: <b>Pro Kampagne</b> nehmen.
          </>,
          <>
            Auf <b>Code kopieren</b> klicken — der komplette Snippet liegt jetzt in deiner
            Zwischenablage.
          </>,
          <>
            In deinem Versand-Tool die Mail öffnen, in den HTML-Editor wechseln und den Code{" "}
            <b>ganz am Ende</b> einfügen, kurz vor <code>&lt;/body&gt;</code>.
          </>,
          <>
            Test-Mail an dich selbst schicken, öffnen — nach 1-2 Minuten siehst du unter{" "}
            <b>Pixel-Hits</b> den Treffer.
          </>,
          <>
            Erst dann an die echte Liste senden. Statistik beobachten unter <b>Dashboard</b> und{" "}
            <b>A/B-Test</b>.
          </>,
        ]}
        tips={[
          <>
            <b>Niemals</b> die E-Mail-Adresse in die URL packen — das triggert sofort Spam-Filter
            und verstößt gegen DSGVO.
          </>,
          <>
            Bounce-Webhook im Versand-Tool eintragen (URL siehst du weiter unten), dann werden tote
            Adressen automatisch markiert.
          </>,
          <>Test-Mail zuerst an Gmail UND Outlook — manchmal blockt nur eines.</>,
          <>
            Wenn du den Snippet änderst, vorher die Kampagne pausieren — sonst werden alte Öffnungen
            falsch gezählt.
          </>,
        ]}
        glossary={[
          {
            term: "Snippet",
            def: "Ein fertiges Stück Code, das du irgendwo einfügst. Hier: HTML, das in deine Mail kommt.",
          },
          {
            term: "Tracking-Pixel",
            def: "Winziges Bild (1x1 Pixel oder hier dein Logo), das beim Öffnen der Mail vom Server geladen wird — so wissen wir, dass die Mail gelesen wurde.",
          },
          {
            term: "Bounce-Webhook",
            def: "Eine Internet-Adresse, an die dein Versand-Tool melden kann, wenn eine Mail abgelehnt wurde. Wir tragen die Adresse dann automatisch in eine Sperrliste ein.",
          },
          {
            term: "List-Unsubscribe",
            def: "Abmelde-Header, den Gmail oben in der Mail als Button anzeigt. Pflicht seit 2024, sonst landet die Mail im Spam.",
          },
          {
            term: "A/B-Test",
            def: "Zwei Varianten parallel testen (z.B. zwei Betreffe), um zu sehen welche besser ankommt.",
          },
        ]}
      />

      <Card className="border-blue-200 bg-blue-50/40 dark:bg-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="w-4 h-4" />
            Tracking-Modi — welchen wählen?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded border bg-background p-3">
              <div className="font-medium mb-1">Modus A — Pro Kampagne (max. Zustellbarkeit)</div>
              <div className="text-xs text-muted-foreground mb-2">
                URL: <code>/a/&lt;campaign_token&gt;.png</code> (gleich für alle Empfänger)
              </div>
              <div className="text-xs">Misst: Gesamt-Opens, kein einzelner Empfänger.</div>
            </div>
            <div className="rounded border bg-background p-3">
              <div className="font-medium mb-1">Modus B — Pro Mail (A/B + opened_at)</div>
              <div className="text-xs text-muted-foreground mb-2">
                URL: <code>/a/&lt;mail_token&gt;.png</code> (neuer Token je Versand)
              </div>
              <div className="text-xs">
                Misst: Wer, wann, Gerät, Provider. Zuordnung nur in der DB —<strong> keine </strong>
                <code>?s=</code> / <code>?r=</code> Query-Parameter in Produktion.
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground border-l-2 border-amber-400 pl-3">
            <strong>Legacy:</strong> <code>?s=&amp;r=</code> nur für alte Templates / manche
            Versand-Tools. Nicht für neue Kampagnen verwenden — Filter und DSGVO triggern auf
            Empfänger-Adressen in der URL.
          </div>
          <div className="text-xs text-muted-foreground border-l-2 border-muted pl-3">
            <strong>Domain-Tipp:</strong> Bild auf derselben Domain hosten wie die From-Adresse (z.
            B. From <code>@anlagenprofis24.de</code> → Bild <code>anlagenprofis24.de/a/…</code>).
            Per CNAME auf <code>meineasykredit.click</code> zeigen — App liefert dann das Pixel
            unter der eigenen Domain aus.
          </div>
          <div className="text-xs text-muted-foreground">
            <strong>Realismus:</strong> Gmail/Apple laden Bilder oft über Proxy und prefetchen —
            Opens sind <em>geschätzt</em>, nicht exakt. Rate-Limit + Dedup laufen serverseitig.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>1. Kampagne auswählen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Kampagne</Label>
            <Select value={campaignId} onValueChange={loadAlias}>
              <SelectTrigger>
                <SelectValue
                  placeholder={campsQ.isLoading ? "Lade Kampagnen…" : "Wähle eine Kampagne…"}
                />
              </SelectTrigger>
              <SelectContent>
                {(campsQ.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {token && (
            <div className="text-sm text-muted-foreground">
              Stealth-Token für diese Kampagne:{" "}
              <code className="bg-muted px-2 py-0.5 rounded">{token}</code>
            </div>
          )}
          {loadingAlias && <div className="text-sm">Token wird angelegt…</div>}
        </CardContent>
      </Card>

      {token && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>2. Snippet-Stil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Stil</Label>
                  <Select value={style} onValueChange={(v) => setStyle(v as SnippetStyle)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(STYLE_LABELS) as SnippetStyle[]).map((s) => (
                        <SelectItem key={s} value={s}>
                          {STYLE_LABELS[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Brand-Name (nur Footer-Brand)</Label>
                  <Input
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="easymail"
                    maxLength={60}
                    disabled={style !== "footer-brand"}
                  />
                </div>
              </div>
              <div>
                <Label>Asset-Domain (neutral, ohne Tracking-Keywords)</Label>
                <Input
                  value={assetHost}
                  onChange={(e) => setAssetHost(e.target.value)}
                  placeholder="img.deinedomain.tld"
                  list="asset-host-suggestions"
                />
                <datalist id="asset-host-suggestions">
                  {DEFAULT_ASSET_HOST_SUGGESTIONS.map((h) => (
                    <option key={h} value={h} />
                  ))}
                </datalist>
                <p className="text-xs text-muted-foreground mt-1">
                  Frei waehlbare Subdomain wie <code>img</code>, <code>assets</code> oder{" "}
                  <code>media</code>. Muss per CNAME/A-Record auf <code>meineasykredit.click</code>{" "}
                  zeigen — dann liefert dieselbe App auch das Pixel aus. Default: Hauptdomain.
                </p>
              </div>
              <div className="flex items-start gap-3 rounded border p-3">
                <input
                  id="short-path"
                  type="checkbox"
                  className="mt-1"
                  checked={shortPath}
                  onChange={(e) => setShortPath(e.target.checked)}
                />
                <div className="flex-1">
                  <Label htmlFor="short-path" className="cursor-pointer">
                    Stealth-Pfad <code>/a/&lt;token&gt;.png</code> (empfohlen)
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Kein <code>/api/public/i/</code> im Pfad, keine E-Mail-Adresse in der URL. Wirkt
                    wie ein normales Bild-Asset. Per-Empfaenger- Aufloesung entfaellt — dafuer
                    deutlich weniger Spam-Signale. Abschalten nur, wenn ihr pro Empfaenger Opens
                    trennen muesst.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>3. HTML-Snippet (direkt in Plusvibe einfügen)</span>
                <Button size="sm" onClick={() => copy(snippet, "Snippet")}>
                  <Copy className="w-4 h-4 mr-2" />
                  Kopieren
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={snippet} readOnly rows={6} className="font-mono text-xs" />
              {validation && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {validation.score === 0 ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Sauber (Score 0)
                      </Badge>
                    ) : validation.score < 15 ? (
                      <Badge variant="secondary">
                        <Info className="w-3 h-3 mr-1" /> OK (Score {validation.score})
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Auffällig (Score{" "}
                        {validation.score})
                      </Badge>
                    )}
                  </div>
                  {validation.warnings.length > 0 && (
                    <ul className="text-xs space-y-1 text-muted-foreground">
                      {validation.warnings.map((w) => (
                        <li key={w.id}>
                          <span
                            className={
                              w.level === "error"
                                ? "text-destructive font-medium"
                                : w.level === "warn"
                                  ? "text-amber-600 font-medium"
                                  : ""
                            }
                          >
                            [{w.level}]
                          </span>{" "}
                          {w.message}
                          {w.hint && <> — {w.hint}</>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Click-Tracking-Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Wickle jeden externen Link in deiner Mail durch unseren Redirect, damit Klicks
                gezählt und Bot-Scans erkannt werden.
              </p>
              <div>
                <Label>Ziel-URL</Label>
                <Input
                  value={clickTarget}
                  onChange={(e) => setClickTarget(e.target.value)}
                  placeholder="https://example.com/landing"
                />
              </div>
              <div>
                <Label>Verwende stattdessen diesen Link in der Mail:</Label>
                <div className="flex gap-2">
                  <Input value={clickUrl} readOnly className="font-mono text-xs" />
                  <Button size="sm" onClick={() => copy(clickUrl, "Click-URL")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. List-Unsubscribe-Header (Plusvibe → Campaign Settings)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Diese zwei Header gehören in die Custom-Headers deiner Plusvibe-Kampagne. Sie
                verbessern Inbox-Placement messbar (Gmail / Outlook bevorzugen RFC-8058-Mails).
              </p>
              <div>
                <Label>List-Unsubscribe</Label>
                <div className="flex gap-2">
                  <Input
                    value={`<${unsubscribeUrl}>, <mailto:unsubscribe+{{lead_id}}@meineasykredit.click>`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() =>
                      copy(
                        `<${unsubscribeUrl}>, <mailto:unsubscribe+{{lead_id}}@meineasykredit.click>`,
                        "List-Unsubscribe",
                      )
                    }
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>List-Unsubscribe-Post</Label>
                <div className="flex gap-2">
                  <Input
                    value="List-Unsubscribe=One-Click"
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => copy("List-Unsubscribe=One-Click", "Post-Header")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Plusvibe-Anleitung</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside text-sm space-y-2">
                <li>
                  In Plusvibe: <b>Campaign → Email Editor</b> öffnen und in den Source-Modus
                  wechseln (<code>&lt;/&gt;</code>-Button).
                </li>
                <li>
                  Das HTML-Snippet aus Schritt 3 <b>ganz unten</b> in den Body einfügen — vor dem
                  schließenden <code>&lt;/body&gt;</code>.
                </li>
                <li>Externe Links in der Mail durch die Click-URL aus Schritt 4 ersetzen.</li>
                <li>
                  Unter <b>Campaign Settings → Custom Headers</b> die beiden Header aus Schritt 5
                  eintragen.
                </li>
                <li>
                  Eine Test-Mail an dich selbst schicken und prüfen, ob sie im <b>Pixel-Debug</b>{" "}
                  auftaucht (Sidebar → Pixel-Debug).
                </li>
                <li>
                  Im Hintergrund läuft der <b>Silent-Bounce-Watchdog</b>: sobald die Kampagne
                  plötzlich keine Opens mehr bekommt, gibt es Telegram-Alarm.
                </li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Skull className="w-4 h-4" /> 7. Bounce-Webhook (warum, wer, wohin)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Damit echte Bounces (Hard / Soft / Block) mit <b>Grund, Absender und Empfänger</b>{" "}
                ankommen, muss Plusvibe (oder dein SMTP-Provider) eingehende Bounces hierher POSTen.
                Bei jedem Bounce: Eintrag im <b>Bounce-Log</b> + <b>sofortige Telegram-Nachricht</b>
                .
              </p>
              <div>
                <Label>Webhook-URL</Label>
                <div className="flex gap-2">
                  <Input value={bounceWebhookUrl} readOnly className="font-mono text-xs" />
                  <Button size="sm" onClick={() => copy(bounceWebhookUrl, "Webhook-URL")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Pflicht-Header</Label>
                <pre className="bg-muted text-xs p-3 rounded font-mono whitespace-pre-wrap">{`Content-Type: application/json
X-Bounce-Signature: <hmac_sha256(BOUNCE_WEBHOOK_SECRET, body)>`}</pre>
                <p className="text-xs text-muted-foreground mt-1">
                  Signatur = HMAC-SHA256 (hex) des rohen JSON-Bodies mit dem geheimen{" "}
                  <code>BOUNCE_WEBHOOK_SECRET</code> (bereits in Lovable Cloud hinterlegt).
                </p>
              </div>
              <div>
                <Label>Beispiel-Payload für diese Kampagne</Label>
                <div className="flex gap-2 items-start">
                  <Textarea
                    value={samplePayload}
                    readOnly
                    rows={14}
                    className="font-mono text-xs"
                  />
                  <Button size="sm" onClick={() => copy(samplePayload, "Beispiel-Payload")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  <b>tag</b> = dein Stealth-Token <code>{token}</code> · der Webhook verknüpft
                  Empfänger und Absender automatisch mit dieser Kampagne, auch wenn die Empfänger
                  vorher nicht registriert waren.
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                Akzeptierte <code>bounce_type</code>-Werte: <code>hard</code>, <code>soft</code>,{" "}
                <code>block</code>. Alle weiteren Felder (diagnostic_code, status_code, remote_mta,
                subject, …) sind optional und werden 1:1 im Bounce-Log + in der Telegram-Nachricht
                angezeigt.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-4 h-4" /> 8. Live-Statistik dieser Kampagne
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {statsQ.isLoading && (
                <div className="text-sm text-muted-foreground">Lade Zahlen…</div>
              )}
              {statsQ.error && (
                <div className="text-sm text-destructive">
                  Fehler: {statsQ.error instanceof Error ? statsQ.error.message : "unbekannt"}
                </div>
              )}
              {statsQ.data && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile label="Versendet (sent)" value={statsQ.data.sent} />
                    <StatTile label="Opens" value={statsQ.data.opens} />
                    <StatTile
                      label="Open-Rate"
                      value={statsQ.data.openRate === null ? "—" : `${statsQ.data.openRate}%`}
                      tone="positive"
                    />
                    <StatTile
                      label="Bounce-Rate"
                      value={statsQ.data.bounceRate === null ? "—" : `${statsQ.data.bounceRate}%`}
                      tone={(statsQ.data.bounceRate ?? 0) > 5 ? "negative" : "neutral"}
                    />
                  </div>
                  {statsQ.data.sent === 0 && (
                    <div className="text-xs text-muted-foreground">
                      Tipp: <code>sent_count</code> ist 0, weil Plusvibe aktuell keine Send-Webhooks
                      pusht. Bounces & Opens werden trotzdem korrekt gezählt; die Raten kommen,
                      sobald <code>sent_count</code> über die Recipients-API oder einen
                      Plusvibe-Sent-Webhook gefüllt wird.
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-muted-foreground">Letzte Bounces (max 20)</Label>
                    {statsQ.data.recentBounces.length === 0 ? (
                      <div className="text-sm text-muted-foreground mt-2">
                        Noch keine Bounces für diese Kampagne — alles sauber.
                      </div>
                    ) : (
                      <div className="mt-2 border rounded overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                              <th className="text-left p-2">Zeit</th>
                              <th className="text-left p-2">Typ</th>
                              <th className="text-left p-2">Empfänger</th>
                              <th className="text-left p-2">Absender</th>
                              <th className="text-left p-2">Grund</th>
                              <th className="text-left p-2">Telegram</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statsQ.data.recentBounces.map((b) => (
                              <tr key={b.id} className="border-t">
                                <td className="p-2 whitespace-nowrap">
                                  {new Date(b.received_at).toLocaleString()}
                                </td>
                                <td className="p-2">
                                  <Badge
                                    variant={b.bounce_type === "hard" ? "destructive" : "secondary"}
                                  >
                                    {b.bounce_type}
                                  </Badge>
                                </td>
                                <td className="p-2">{b.recipient_email}</td>
                                <td className="p-2">{b.sender_email ?? "—"}</td>
                                <td
                                  className="p-2 max-w-[280px] truncate"
                                  title={b.bounce_reason ?? ""}
                                >
                                  {b.bounce_reason ?? "—"}
                                </td>
                                <td className="p-2">
                                  {b.telegram_sent ? (
                                    <Badge className="bg-emerald-600">gesendet</Badge>
                                  ) : (
                                    <Badge variant="destructive">offen</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "positive" | "negative" | "neutral";
}) {
  const color =
    tone === "positive"
      ? "text-emerald-600"
      : tone === "negative"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="border rounded p-3 bg-card/40">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
